import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { secondaryCriteriaTemplate } from "@planzone/dto";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    namespace,
    scope,
    elementType,
  }: Boundaries["retrieveSecondaryCriteriaTemplate"]["request"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === elementType,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          namespace,
          scope,
          type: elementType,
          request: setRequestStateToPending(),
          template: secondaryCriteriaTemplate,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    namespace,
    elementType,
    scope,
    template,
  }: Boundaries["retrieveSecondaryCriteriaTemplate"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === elementType,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          namespace,
          scope,
          type: elementType,
          request: setRequestStateToSuccess(),
          template,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          namespace,
          scope,
          type: elementType,
          request: setRequestStateToSuccess(),
          template,
        },
      ];
}

function handleFailure(
  state: State,
  {
    namespace,
    elementType,
    scope,
  }: Boundaries["retrieveSecondaryCriteriaTemplate"]["failure"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === elementType,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], scope, request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          namespace,
          type: elementType,
          scope,
          request: setRequestStateToFailure(),
          template: secondaryCriteriaTemplate,
        },
      ];
}

function handleEdition(
  state: State,
  {
    template,
    scope,
    elementType,
    namespace,
  }: Boundaries["editSecondaryCriteriaTemplate"]["success"],
): State {
  const index = state.findIndex(
    (req) => req.namespace === namespace && req.type === elementType,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], template },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          namespace,
          type: elementType,
          scope,
          request: setRequestStateToSuccess(),
          template,
        },
      ];
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveSecondaryCriteriaTemplateRequested]: handleRequest,
    [events.retrieveSecondaryCriteriaTemplateSucceeded]: handleSuccess,
    [events.retrieveSecondaryCriteriaTemplateFailed]: handleFailure,
    [events.editSecondaryCriteriaTemplateSucceeded]: handleEdition,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
